import React from 'react';
import { SourceBreakdownData } from '../types';
interface SourceBreakdownProps {
  data: SourceBreakdownData[];
  onSourceClick?: (source: SourceBreakdownData) => void;
}
const formatCurrency = (n: number) => {
  if (n === 0) return '—';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};
const formatFullCurrency = (n: number) => n === 0 ? '—' : `$${n.toLocaleString()}`;
export function SourceBreakdown({
  data,
  onSourceClick
}: SourceBreakdownProps) {
  const totalCreated = data.reduce((s, x) => s + x.created, 0);
  const totalConverted = data.reduce((s, x) => s + x.converted, 0);
  const totalFunded = data.reduce((s, x) => s + x.funded, 0);
  const totalCreatedAmount = data.reduce((s, x) => s + x.createdAmount, 0);
  const totalConvertedAmount = data.reduce((s, x) => s + x.convertedAmount, 0);
  const totalFundedAmount = data.reduce((s, x) => s + x.fundedAmount, 0);
  const avgLoanAmount = totalFunded > 0 ? totalFundedAmount / totalFunded : 0;
  return <div className="space-y-6" data-id="element-245">
      {/* Source Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-246">
        <div className="flex items-center justify-between p-4 border-b border-gray-100" data-id="element-247">
          <h2 className="text-base font-bold text-[#3d3d3d] flex items-center gap-2" data-id="element-248">
            <div className="w-1.5 h-5 bg-[#5a7fa5] rounded-sm" data-id="element-249"></div>
            List Source Breakdown
          </h2>
          <span className="text-xs text-gray-400 font-medium" data-id="element-250">
            Click a row to filter dashboard
          </span>
        </div>

        <div className="overflow-x-auto" data-id="element-251">
          <table className="w-full text-sm text-left" data-id="element-252">
            <thead className="text-[10px] text-gray-500 uppercase border-b border-gray-200 bg-gray-50/50" data-id="element-253">
              <tr data-id="element-254">
                <th className="px-4 py-3 font-semibold tracking-wider" data-id="element-255">
                  SOURCE
                </th>
                <th className="px-4 py-3 font-semibold tracking-wider text-right" data-id="element-256">
                  CREATED
                  <div className="text-[9px] text-gray-400 font-normal normal-case tracking-normal mt-0.5" data-id="element-257">
                    # / $
                  </div>
                </th>
                <th className="px-4 py-3 font-semibold tracking-wider text-right" data-id="element-258">
                  CONVERTED
                  <div className="text-[9px] text-gray-400 font-normal normal-case tracking-normal mt-0.5" data-id="element-259">
                    # / $
                  </div>
                </th>
                <th className="px-4 py-3 font-semibold tracking-wider text-right" data-id="element-260">
                  FUNDED
                  <div className="text-[9px] text-gray-400 font-normal normal-case tracking-normal mt-0.5" data-id="element-261">
                    # / $
                  </div>
                </th>
                <th className="px-4 py-3 font-semibold tracking-wider text-right" data-id="element-262">
                  AVG LOAN
                </th>
                <th className="px-4 py-3 font-semibold tracking-wider text-right" data-id="element-263">
                  CONV. RATE
                </th>
                <th className="px-4 py-3 font-semibold tracking-wider text-right" data-id="element-264">
                  PULL-THROUGH
                </th>
              </tr>
            </thead>
            <tbody data-id="element-265">
              {data.map((source, index) => <tr key={source.id} onClick={() => onSourceClick?.(source)} className={`border-b border-gray-100 hover:bg-[#c9a84c]/5 cursor-pointer transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`} data-id="element-266">
                  <td className="px-4 py-3.5 font-semibold text-gray-800" data-id="element-267">
                    <div className="flex items-center gap-2.5" data-id="element-268">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                    backgroundColor: source.color
                  }} data-id="element-269"></div>
                      {source.source}
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-right" data-id="element-270">
                    <div className="font-bold text-gray-800" data-id="element-271">
                      {source.created.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 font-medium" data-id="element-272">
                      {formatCurrency(source.createdAmount)}
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-right" data-id="element-273">
                    <div className="font-bold text-gray-800" data-id="element-274">
                      {source.converted.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 font-medium" data-id="element-275">
                      {formatCurrency(source.convertedAmount)}
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-right" data-id="element-276">
                    <div className="font-bold text-[#c9a84c]" data-id="element-277">
                      {source.funded}
                    </div>
                    <div className="text-xs text-gray-500 font-medium" data-id="element-278">
                      {formatCurrency(source.fundedAmount)}
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-right font-medium text-gray-700" data-id="element-279">
                    {formatCurrency(source.avgAmount)}
                  </td>
                  <td className="px-4 py-3.5 text-right font-medium text-gray-700" data-id="element-280">
                    {source.convRate}%
                  </td>
                  <td className="px-4 py-3.5" data-id="element-281">
                    <div className="flex items-center justify-end gap-2" data-id="element-282">
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden flex-shrink-0" data-id="element-283">
                        <div className="h-full bg-[#5a7fa5] rounded-full" style={{
                      width: `${Math.min(source.pullThrough * 10, 100)}%`
                    }} data-id="element-284"></div>
                      </div>
                      <span className="text-gray-700 w-8 text-right font-bold text-xs" data-id="element-285">
                        {source.pullThrough}%
                      </span>
                    </div>
                  </td>
                </tr>)}
              {data.length === 0 && <tr data-id="element-286">
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-400" data-id="element-287">
                    No sources match the current filter.
                  </td>
                </tr>}
            </tbody>
            <tfoot data-id="element-288">
              <tr className="bg-[#3d3d3d] text-white font-bold" data-id="element-289">
                <td className="px-4 py-3 text-xs uppercase tracking-wider" data-id="element-290">
                  Total
                </td>
                <td className="px-4 py-3 text-right" data-id="element-291">
                  <div data-id="element-292">{totalCreated.toLocaleString()}</div>
                  <div className="text-xs text-gray-300 font-medium" data-id="element-293">
                    {formatCurrency(totalCreatedAmount)}
                  </div>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-294">
                  <div data-id="element-295">{totalConverted.toLocaleString()}</div>
                  <div className="text-xs text-gray-300 font-medium" data-id="element-296">
                    {formatCurrency(totalConvertedAmount)}
                  </div>
                </td>
                <td className="px-4 py-3 text-right text-[#c9a84c]" data-id="element-297">
                  <div data-id="element-298">{totalFunded}</div>
                  <div className="text-xs text-gray-300 font-medium" data-id="element-299">
                    {formatCurrency(totalFundedAmount)}
                  </div>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-300">
                  {formatCurrency(Math.round(avgLoanAmount))}
                </td>
                <td className="px-4 py-3 text-right" data-id="element-301">—</td>
                <td className="px-4 py-3 text-right" data-id="element-302">—</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Funded Distribution */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-303">
        <div className="flex items-center justify-between p-4 border-b border-gray-100" data-id="element-304">
          <h2 className="text-sm font-bold text-[#3d3d3d]" data-id="element-305">
            Funded Volume Distribution
          </h2>
          <span className="text-xs text-[#c9a84c] font-bold" data-id="element-306">
            {formatFullCurrency(totalFundedAmount)} total
          </span>
        </div>
        <div className="p-4" data-id="element-307">
          <div className="flex h-7 rounded-lg overflow-hidden bg-gray-100" data-id="element-308">
            {data.filter(s => s.fundedAmount > 0).map(source => <div key={source.id} className="h-full flex items-center justify-center text-white text-[10px] font-bold transition-all hover:opacity-90" style={{
            width: `${totalFundedAmount > 0 ? source.fundedAmount / totalFundedAmount * 100 : 0}%`,
            backgroundColor: source.color,
            minWidth: source.fundedAmount > 0 ? '60px' : '0'
          }} data-id="element-309">
                  {source.source} · {formatCurrency(source.fundedAmount)}
                </div>)}
          </div>
          <div className="flex flex-wrap gap-4 mt-3" data-id="element-310">
            {data.map(source => <div key={source.id} className="flex items-center gap-1.5 text-xs text-gray-600" data-id="element-311">
                <div className="w-2 h-2 rounded-full" style={{
              backgroundColor: source.color
            }} data-id="element-312"></div>
                <span className="font-medium" data-id="element-313">{source.source}</span>
                <span className="text-gray-400" data-id="element-314">
                  ({source.funded} · {formatCurrency(source.fundedAmount)})
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}